import { useEffect, useState, useCallback } from "react";
import UserLayout from "@/components/UserLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Copy, X, Check, Clock, RefreshCw, MessageSquare, Repeat, Bell } from "lucide-react";
import { ServiceIcon } from "@/components/ServiceIcon";

const CANCEL_AFTER_MIN = 4;
const AUTO_REFUND_MIN = 20;

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function elapsedMinutes(createdAt, now) {
  if (!createdAt) return 0;
  const created = new Date(createdAt).getTime();
  return (now - created) / 60000;
}

function formatMS(minutesLeft) {
  const total = Math.max(0, Math.round(minutesLeft * 60));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function MyNumbers() {
  const { refresh } = useAuth();
  const [numbers, setNumbers] = useState([]);
  const [notifOn, setNotifOn] = useState(false);
  const now = useNow(1000);

  useEffect(() => {
    // Detect existing browser notification permission
    if (typeof window !== "undefined" && "Notification" in window) {
      setNotifOn(Notification.permission === "granted");
    }
  }, []);

  const enableNotifications = async () => {
    if (!("Notification" in window)) {
      toast.error("Tarayıcınız bildirim desteklemiyor");
      return;
    }
    if (Notification.permission === "granted") { setNotifOn(true); return; }
    const p = await Notification.requestPermission();
    setNotifOn(p === "granted");
    if (p === "granted") toast.success("Bildirimler aktifleştirildi 🔔");
  };

  const playDing = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 1);
    } catch (e) { console.warn("SMS notification sound failed:", e); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/numbers/my");
      setNumbers(data);
    } catch (e) { console.warn("Numaralar yüklenemedi:", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll SMS status for waiting numbers every 5s (also refresh balance if refund happened)
  useEffect(() => {
    const waiting = numbers.filter((n) => n.status === "waiting");
    if (waiting.length === 0) return;
    const interval = setInterval(async () => {
      for (const n of waiting) {
        try {
          const { data } = await api.get(`/numbers/${n.id}/check`);
          setNumbers((prev) => {
            const current = prev.find((x) => x.id === n.id);
            const newSms = data.sms_code;
            
            // SMS ilk kez geldiyse ses çal
            if (newSms && newSms !== current?.sms_code) {
              playDing();

              if (Notification.permission === "granted") {
                try {
                  new Notification("SMS Kodu Geldi 🔔", {
                    body: `Kod: ${newSms}`,
                    icon: "/favicon.ico",
                  });
                } catch (e) {
                  console.warn("Browser notification failed:", e);
                }
              }

              toast.success(`SMS kodu geldi: ${newSms}`);
            }

            return prev.map((x) =>
              x.id === n.id
                ? {
                    ...x,
                    status: data.status,
                    sms_code: newSms ?? x.sms_code
                  }
                : x
            );
          });
        } catch (e) { console.warn(`SMS check failed for ${n.id}:`, e); }
      }
      // reconcile in case auto-refund kicked in on backend
      load();
      refresh();
    }, 5000);
    return () => clearInterval(interval);
  }, [numbers, load, refresh]);

  const cancel = async (id) => {
    try {
      const { data } = await api.post(`/numbers/${id}/cancel`);
      toast.success(data.refunded ? `İptal edildi. ₺${data.refunded.toFixed(2)} iade edildi.` : "İptal edildi");
      await refresh();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const finish = async (id) => {
    try {
      await api.post(`/numbers/${id}/finish`);
      toast.success("Tamamlandı olarak işaretlendi");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const rebuy = async (n) => {
    try {
      const { data } = await api.post("/numbers/buy", {
        country_id: n.country_id,
        service: n.service,
        max_price: n.raw_cost,
        provider: n.provider,
        tier: n.tier || null,
      });
      toast.success(`Yeni numara alındı: +${data.number.phone}`);
      await refresh();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Kopyalandı");
  };

  const waiting = numbers.filter((n) => n.status === "waiting");
  const others = numbers.filter((n) => n.status !== "waiting");

  return (
    <UserLayout>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Aktif işlemler</div>
          <h1 className="font-display text-3xl md:text-4xl font-black">Alınanlar</h1>
          <p className="text-white/50 text-sm mt-2">
            SMS kodu 5 saniyede bir kontrol edilir. Kod gelmezse {AUTO_REFUND_MIN}. dakikada tam iade otomatik yapılır.
          </p>
        </div>
        <button
          onClick={enableNotifications}
          data-testid="enable-notifications"
          className={`inline-flex items-center gap-2 border rounded-xl px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
            notifOn ? "border-[hsl(142,71%,55%)]/50 text-[hsl(142,71%,55%)] bg-[hsl(142,71%,55%)]/10" : "border-white/15 text-white/70 hover:border-white/40"
          }`}
        >
          <Bell className="w-3.5 h-3.5" /> {notifOn ? "Bildirim: Açık" : "Bildirimi Aç"}
        </button>
      </div>

      {numbers.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-2xl p-12 text-center text-white/50">
          Henüz numara almadınız.
        </div>
      ) : (
        <div className="space-y-4">
          {waiting.map((n) => (
            <ActiveCard key={n.id} n={n} now={now} onCancel={cancel} onFinish={finish} onCopy={copy} onRebuy={rebuy} />
          ))}
          {others.length > 0 && (
            <div className="pt-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Geçmiş</div>
              <div className="border border-white/10 bg-[hsl(0,0%,7%)] rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-[0.2em] text-white/40 border-b border-white/10 bg-white/[0.02]">
                    <tr>
                      <th className="text-left py-3 px-4 font-bold">Numara</th>
                      <th className="text-left px-4 font-bold">Servis</th>
                      <th className="text-left px-4 font-bold">Ülke</th>
                      <th className="text-right px-4 font-bold">Fiyat</th>
                      <th className="text-left px-4 font-bold">SMS</th>
                      <th className="text-left px-4 font-bold">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {others.map((n) => (
                      <tr key={n.id} className="border-b border-white/5 last:border-0">
                        <td className="py-3 px-4">
                          <button onClick={() => copy(n.phone)} className="font-mono hover:text-[hsl(217,91%,63%)] inline-flex items-center gap-1.5">
                            +{n.phone} <Copy className="w-3 h-3" />
                          </button>
                        </td>
                        <td className="px-4">
                          <span className="inline-flex items-center gap-2">
                            <ServiceIcon code={n.service} size="xs" />
                            <span className="uppercase text-xs tracking-wider text-white/70">{n.service}</span>
                          </span>
                        </td>
                        <td className="px-4 text-white/70">{n.country_name}</td>
                        <td className="px-4 text-right font-mono">₺{Number(n.price).toFixed(2)}</td>
                        <td className="px-4">
                          {n.sms_code ? (
                            <button onClick={() => copy(n.sms_code)} className="font-mono text-[hsl(142,71%,55%)] font-bold inline-flex items-center gap-1.5">
                              {n.sms_code} <Copy className="w-3 h-3" />
                            </button>
                          ) : (
                            <span className="text-white/30">—</span>
                          )}
                        </td>
                        <td className="px-4">
                          <div className="flex items-center gap-2">
                            <StatusPill s={n.status} />
                            <button
                              onClick={() => rebuy(n)}
                              data-testid={`history-rebuy-${n.id}`}
                              title="Aynı ülke ve fiyat kademesinden yeniden al"
                              className="inline-flex items-center gap-1.5 border border-[hsl(43,74%,55%)]/60 text-[hsl(43,74%,65%)] hover:bg-[hsl(43,74%,55%)]/10 px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-lg font-bold transition-colors whitespace-nowrap"
                            >
                              <Repeat className="w-3 h-3" />
                              Yeniden Al
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </UserLayout>
  );
}

function ActiveCard({ n, now, onCancel, onFinish, onCopy, onRebuy }) {
  const elapsed = elapsedMinutes(n.created_at, now);
  const remainingToCancel = Math.max(0, CANCEL_AFTER_MIN - elapsed);
  const remainingToAutoRefund = Math.max(0, AUTO_REFUND_MIN - elapsed);
  const canCancel = elapsed >= CANCEL_AFTER_MIN && !n.sms_code;
  const hasCode = !!n.sms_code;

  return (
    <div className="border border-white/10 bg-[hsl(220,40%,9%)] rounded-2xl p-5 md:p-6">
      {/* Top row: phone + service info */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center">
            <ServiceIcon code={n.service} size="md" />
          </div>
          <div>
            <button onClick={() => onCopy(n.phone)} className="font-mono font-bold text-xl md:text-2xl hover:text-[hsl(43,74%,65%)] inline-flex items-center gap-2 transition-colors">
              +{n.phone} <Copy className="w-4 h-4 text-white/40" />
            </button>
            <div className="text-xs text-white/50 mt-1">
              {n.country_name} · <span className="uppercase tracking-wider">{n.service}</span> · ₺{Number(n.price).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Status pill top-right */}
        {hasCode ? (
          <div className="inline-flex items-center gap-2 text-[hsl(142,71%,55%)] text-xs font-bold uppercase tracking-[0.15em]">
            <span className="w-2 h-2 rounded-full bg-[hsl(142,71%,55%)]" />
            SMS Alındı
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 text-[hsl(43,74%,65%)] text-xs font-bold uppercase tracking-[0.15em]">
            <span className="pulse-dot" style={{ background: "hsl(43 74% 55%)" }} />
            SMS Bekleniyor
          </div>
        )}
      </div>

      {/* Prominent SMS code display area — ALWAYS visible */}
      <div className={`mt-5 rounded-2xl p-5 border transition-colors ${
        hasCode
          ? "border-[hsl(142,71%,45%)]/60 bg-[hsl(142,71%,45%)]/10"
          : "border-white/10 bg-black/30 border-dashed"
      }`}>
        <div className={`text-[10px] uppercase tracking-[0.25em] mb-2 flex items-center gap-2 ${hasCode ? "text-[hsl(142,71%,65%)]" : "text-white/40"}`}>
          <MessageSquare className="w-3.5 h-3.5" /> SMS Doğrulama Kodu
        </div>
        {hasCode ? (
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => onCopy(n.sms_code)}
              data-testid={`sms-code-${n.id}`}
              className="font-mono text-3xl md:text-4xl font-black text-[hsl(142,71%,55%)] tracking-widest inline-flex items-center gap-3"
            >
              {n.sms_code}
              <Copy className="w-5 h-5 text-[hsl(142,71%,55%)]/70" />
            </button>
            <span className="text-[10px] uppercase tracking-wider text-[hsl(142,71%,65%)] bg-[hsl(142,71%,45%)]/20 border border-[hsl(142,71%,45%)]/40 px-2 py-1 rounded">
              Kopyala →
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="font-mono text-2xl md:text-3xl text-white/25 tracking-widest">— — — — —</div>
            <div className="text-xs text-white/50">
              Geçen: <span className="font-mono text-white/80">{Math.floor(elapsed)}dk {Math.floor((elapsed - Math.floor(elapsed)) * 60)}sn</span>
            </div>
          </div>
        )}
      </div>

      {/* Countdown grid — hidden when code arrives */}
      {!hasCode && (
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
          <div className="border border-white/10 rounded-xl p-3">
            <div className="text-white/40 uppercase tracking-[0.15em] text-[10px] mb-1 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> İptal İçin Kalan
            </div>
            <div className={`font-mono font-black text-lg ${canCancel ? "text-[hsl(142,71%,55%)]" : "text-white"}`}>
              {canCancel ? "AKTİF" : formatMS(remainingToCancel)}
            </div>
          </div>
          <div className="border border-[hsl(43,74%,55%)]/30 rounded-xl p-3 bg-[hsl(43,74%,55%)]/5">
            <div className="text-[hsl(43,74%,65%)] uppercase tracking-[0.15em] text-[10px] mb-1 flex items-center gap-1.5">
              <RefreshCw className="w-3 h-3" /> Otomatik İade
            </div>
            <div className="font-mono font-black text-lg text-[hsl(43,74%,65%)]">{formatMS(remainingToAutoRefund)}</div>
          </div>
        </div>
      )}

      {/* Action buttons — ALWAYS visible */}
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <button
          onClick={() => onRebuy(n)}
          data-testid={`rebuy-${n.id}`}
          title="Aynı ülke ve fiyat kademesinden yeni numara al"
          className="border border-[hsl(43,74%,55%)]/60 text-[hsl(43,74%,65%)] hover:bg-[hsl(43,74%,55%)]/10 text-xs px-4 py-2 uppercase tracking-wider transition-colors rounded-xl font-bold"
        >
          <Repeat className="w-3.5 h-3.5 inline mr-1" />Yeniden Al
        </button>
        {hasCode && (
          <button
            onClick={() => onFinish(n.id)}
            data-testid={`finish-${n.id}`}
            className="border border-[hsl(142,71%,45%)]/50 text-[hsl(142,71%,55%)] hover:bg-[hsl(142,71%,45%)]/10 text-xs px-4 py-2 uppercase tracking-wider transition-colors rounded-xl font-bold"
          >
            <Check className="w-3.5 h-3.5 inline mr-1" />Tamamladım
          </button>
        )}
        <button
          onClick={() => onCancel(n.id)}
          disabled={!canCancel}
          data-testid={`cancel-${n.id}`}
          title={hasCode ? "Kod alındıktan sonra iade yapılamaz" : (!canCancel ? `${CANCEL_AFTER_MIN}. dakikadan önce iptal edilemez` : "İptal et ve iade al")}
          className={`text-xs px-4 py-2 uppercase tracking-wider transition-colors rounded-xl font-bold ${
            canCancel
              ? "border border-red-500/60 text-red-400 hover:bg-red-500/10"
              : "border border-white/10 bg-white/[0.02] text-white/30 cursor-not-allowed"
          }`}
        >
          <X className="w-3.5 h-3.5 inline mr-1" />
          {canCancel ? "İptal & İade" : hasCode ? "İade Edilemez" : `İptal (${formatMS(remainingToCancel)})`}
        </button>
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    waiting: { label: "SMS Bekliyor", cls: "text-[hsl(38,92%,60%)] border-[hsl(38,92%,60%)]/40" },
    received: { label: "SMS Geldi", cls: "text-[hsl(142,71%,55%)] border-[hsl(142,71%,55%)]/40" },
    cancelled: { label: "İade", cls: "text-white/40 border-white/20" },
    finished: { label: "Tamamlandı", cls: "text-white/60 border-white/20" },
  };
  const m = map[s] || map.waiting;
  return (
    <span className={`inline-flex items-center gap-2 border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded ${m.cls}`}>{m.label}</span>
  );
}
